<!--
  File name: PBNEFListItem.vue
  Program Description:
    - This component renders the specific PBNEF item used in the list
-->
<template>
  <span>
    <v-card
      elevation="4"
      class="pa-6 mb-6"
      :style="{ cursor: disabled ? 'default' : 'cursor' }"
      @click.stop="viewPBNEF"
    >
      <h3>
        Preliminary Breach Notification Evaluation Form - {{ item.docketNumber }}
        <v-chip
          v-if="status(item.status) && stageStatus(item)"
          disabled
          class="ml-3"
          :class="getPBNEFStatusProperties(status(item.status).value, item.approveReject).chip"
          :outlined="getPBNEFStatusProperties(status(item.status).value, item.approveReject).outlined"
        >
          <span
            :class="getPBNEFStatusProperties(status(item.status).value, item.approveReject).text"
          >
            {{ stageStatus(item) }}
          </span>
        </v-chip>
      </h3>
      <h4>{{ item.requestFor.map(formattedTypeOfRequestText).join(', ') }}</h4>
      <span class="gray--text">Saved on {{ item.dateUpdated }}</span>
      <template v-if="item.fileUpload && $s.userRole.includes('pic')">
        <h4 class="mt-1">Resolution</h4>
        <FileInputUpload
          input-class="required-field"
          :value="[item.fileUpload]"
          :dense="true"
          :clearable="true"
          :can-edit="false"
          :show-download="true"
          :entry-id="item._id"
          accept=".pdf"
          label="Resolution"
          file-id="fileUpload"
          field-name="fileUpload"
          collection="pbnef"
          can-view-file
        />
      </template>
    </v-card>
  </span>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import crud from '@/utils/rest/crud'
import { toOrdinal } from '@/utils/helpers.js'
export default {
  name: 'ViewListItem',
  props: {
    item: {
      type: Object,
      default: () => {}
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapFields('pbnef', {
      dbnStatus: 'dbnStatus'
    }),
    formattedTypeOfRequestText () {
      return text => {
        if (text === 'Request for exemption') return `${text} to notify data subject`
        if (!text.includes('Request for extension')) return text
        const numberOfRequest = text.split(' ')[0].replace(/[{()}]/g, '')
        if (isNaN(parseInt(numberOfRequest))) return text
        return toOrdinal(numberOfRequest) + ' Request for extension'
      }
    }
  },
  methods: {
    status (id) {
      return this.dbnStatus.find(x => x._id === id)
    },
    viewPBNEF () {
      if (this.disabled) return
      this.$router.push({
        name: 'PBNEFView',
        params: {
          operation: 'view',
          id: this.item._id
        }
      })
    },
    stageStatus (item) {
      if (!this.status(item.status)) return

      switch (this.status(item.status).value) {
        case 'Initial Review - PBNEF':
          return 'Submitted'
        case 'Final Review - PBNEF':
          return 'Reviewed by Initial Reviewer'
        case 'For Approval - PBNEF':
          return 'Endorsed to Adjudication'
        case 'For Evaluation':
        case 'Initial Order':
          return item.approveReject
        default:
          return this.status(item.status).value
      }
    },
    getPBNEFStatusProperties (status, approval) {
      const statusObject = { chip: '', text: '', outlined: false }

      switch (status) {
        case 'Drafted': // Drafted
          statusObject.chip = 'blue lighten-5'
          statusObject.text = 'blue--text text--darken-1'
          break
        case 'Initial Review - PBNEF': // Submitted PBNEF
          statusObject.chip = 'green lighten-5'
          statusObject.text = 'green--text text--darken-1'
          break
        case 'Final Review - PBNEF': // Reviewed by initial reviewer
          statusObject.chip = 'orange lighten-5'
          statusObject.text = 'orange--text text--darken-4'
          break
        case 'For Approval - PBNEF': // Endorsed to Adjudication
          statusObject.chip = 'yellow lighten-4'
          statusObject.text = 'yellow--text text--darken-4'
          break
        case 'For Evaluation': // Approved/Rejected
        case 'Initial Order':
          if (approval === 'Rejected') {
            statusObject.chip = 'red lighten-5'
            statusObject.text = 'red--text text--darken-1'
            statusObject.outlined = true
          } else {
            statusObject.chip = 'green lighten-5'
            statusObject.text = 'green--text text--darken-1'
            statusObject.outlined = true
          }
          break
        default:
          break
      }
      return statusObject
    },
    async downloadFile (item) {
      const { _id, fileUpload } = item
      const routeAttachment = `webapi/attachment/pbnef/${_id}/fileUpload/${fileUpload.filename}`
      let url = routeAttachment
      if (url.startsWith('webapi/')) url = routeAttachment.substring(7)
      const resp = await crud(url).get()
      if (resp.ok) {
        const data = await resp.json()
        const downloadLink = document.createElement('a')
        downloadLink.href = data.file
        downloadLink.download = fileUpload.filename
        downloadLink.click()
      }
    }
  }
}
</script>
